import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, BoxExtendedProps, ThreeDotIcon, useClickOutside } from '@common-fe/common-fe';
import styled from 'styled-components';

import spacings from '@/styles/spacings';

const StyledBox = styled(Box)`
  ${({ theme }) => theme.border.blueGray2}
  ${({ theme }) => theme.shadows.active}
`;

const StyledIconBox = styled(Box)`
  cursor: pointer;
  &:hover {
    svg {
      fill: ${({ theme }) => theme.colors.iconAccent};
      transition: all 0.2s ease-in-out;
    }
  }
`;

interface Props extends BoxExtendedProps {
  contentStyle?: React.CSSProperties;
  children?: React.ReactNode;
  testId?: string;
  isExpanded?: boolean;
  setIsExpanded?: (isExpanded: boolean) => void;
}

export const DropButton: React.FC<Props> = ({
  children,
  contentStyle,
  testId,
  isExpanded,
  setIsExpanded,
  ...props
}) => {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(!!isExpanded);

  useClickOutside(ref, () => {
    setExpanded(false);
  });

  useEffect(() => {
    setExpanded(!!isExpanded);
  }, [isExpanded]);

  const handleExpand = useCallback(() => {
    setExpanded(!expanded);
    setIsExpanded?.(!expanded);
  }, [expanded, setIsExpanded]);

  return (
    <Box
      data-testId={`DropButton_${testId}`}
      style={{
        position: 'relative',
      }}
      ref={ref}
    >
      {expanded && (
        <StyledBox
          data-testid="DropButton_content_container"
          pad={{ horizontal: 'm', vertical: 'm' }}
          background="canvas"
          round={spacings.xs}
          width={{ min: '200px', max: '200px' }}
          height={{ min: '45px', max: '90px' }}
          style={{
            position: 'absolute',
            top: '-65px',
            left: '-160px',
            ...contentStyle,
          }}
          {...props}
        >
          {children}
        </StyledBox>
      )}

      <StyledIconBox data-testid="DropButton_testid" onClick={handleExpand}>
        <ThreeDotIcon />
      </StyledIconBox>
    </Box>
  );
};
